/** Drawn here rather than pulled in as a package: there are a handful of them,
 *  they are all a path or two, and an icon font would be a second network round
 *  trip on the first screen. They take their colour from the text around them. */

type Props = { size?: number; className?: string };

function Svg({ size = 20, className, children }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const Back = (p: Props) => (
  <Svg {...p} size={p.size ?? 22}>
    <path d="M15 5l-7 7 7 7" />
  </Svg>
);

export const Chevron = (p: Props) => (
  <Svg {...p} size={p.size ?? 18}>
    <path d="M9 5l7 7-7 7" />
  </Svg>
);

export const Plus = (p: Props) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const Close = (p: Props) => (
  <Svg {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </Svg>
);

export const Check = (p: Props) => (
  <Svg {...p}>
    <path d="M4 12.5l5 5L20 6.5" />
  </Svg>
);

export const Inbox = (p: Props) => (
  <Svg {...p} size={p.size ?? 34}>
    <path d="M3 13h5l1.5 3h5L16 13h5" />
    <path d="M5.4 5.7A2 2 0 017.2 4.5h9.6a2 2 0 011.8 1.2L21 13v4.5a2 2 0 01-2 2H5a2 2 0 01-2-2V13z" />
  </Svg>
);

/** The iOS share glyph, so the add-to-home-screen tip can point at the button
 *  it actually means rather than describing it. */
export const Cube = (p: Props) => (
  <Svg {...p} size={p.size ?? 16}>
    <path d="M12 2.6 3.6 7v10L12 21.4 20.4 17V7z" />
    <path d="M3.6 7 12 11.4 20.4 7" />
    <path d="M12 11.4v10" />
  </Svg>
);

export const Share = (p: Props) => (
  <Svg {...p} size={p.size ?? 16}>
    <path d="M12 15V3" />
    <path d="M8 6.5L12 2.5l4 4" />
    <path d="M6 12H5a2 2 0 00-2 2v6a2 2 0 002 2h14a2 2 0 002-2v-6a2 2 0 00-2-2h-1" />
  </Svg>
);

export const Bell = (p: Props) => (
  <Svg {...p}>
    <path d="M18 9a6 6 0 10-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
    <path d="M13.7 20a2 2 0 01-3.4 0" />
  </Svg>
);

/** Three bars, for the numbers. */
export const Chart = (p: Props) => (
  <Svg {...p}>
    <path d="M5 20v-8M12 20V5M19 20v-5" />
  </Svg>
);

/** An eye, for how the shop is seen: on Google, Bing and in AI answers. */
export const Eye = (p: Props) => (
  <Svg {...p}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

/** A clock turning back, for the earlier versions of a drawing. */
export const History = (p: Props) => (
  <Svg {...p}>
    <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </Svg>
);

/** A pencil, for a small fix to a drawing. */
export const Pencil = (p: Props) => (
  <Svg {...p}>
    <path d="M4 20h4L19 9a2.83 2.83 0 00-4-4L4 16v4z" />
    <path d="M13.5 6.5l4 4" />
  </Svg>
);

/** Two sliders, for the settings of a draw. */
export const Sliders = (p: Props) => (
  <Svg {...p}>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <circle cx="15" cy="7" r="2" />
    <circle cx="9" cy="17" r="2" />
  </Svg>
);

/** A magnifier, for the search over the list. */
export const Search = (p: Props) => (
  <Svg {...p} size={p.size ?? 18}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M20 20l-4.2-4.2" />
  </Svg>
);

/** An envelope, for the newsletter list. */
export const Mail = (p: Props) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </Svg>
);

/** A speech bubble, for asking about the numbers. */
export const Ask = (p: Props) => (
  <Svg {...p}>
    <path d="M20 12a8 8 0 01-11.6 7.1L4 20l1-4.1A8 8 0 1120 12z" />
    <path d="M9 11h.01M12 11h.01M15 11h.01" />
  </Svg>
);
